import React, { useEffect, useState } from "react";
import { Camera, Upload, ArrowRight, Loader2, MessageCircle, CheckCircle2, X, Sparkles, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { api } from "../lib/api";
import { track, trackCustom } from "../lib/pixel";
import { toast } from "sonner";

const JOB_TYPE_LABEL = {
  single_item: "Einzelnes Stück",
  single_medium: "Einzelstück (mittel)",
  single_heavy: "Einzelstück (schwer)",
  few_items: "Wenige Gegenstände",
  many_boxes_furniture: "Viele Kartons + Möbel",
  complete_room: "Komplettes Zimmer",
  complete_kitchen: "Komplette Küche",
  many_large_furniture: "Viele große Möbel",
  partial_move: "Teil-Umzug",
  full_move: "Kompletter Umzug",
};

const EFFORT_LABEL = { leicht: "Leicht", mittel: "Mittel", schwer: "Hoch" };
const EFFORT_COLOR = {
  leicht: "bg-emerald-100 text-emerald-800 border-emerald-200",
  mittel: "bg-amber-100 text-amber-900 border-amber-200",
  schwer: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function Scan() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]); // { file, previewUrl }
  const [type, setType] = useState("small");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    track({ custom: "ScannerOpened", params: { content_name: "Scanner" } });
  }, []);

  const onPick = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 8);
    const enriched = list.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    setFiles((prev) => [...prev, ...enriched].slice(0, 8));
    setResult(null);
  };

  const removeAt = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const runScan = async () => {
    if (files.length === 0) {
      toast.error("Bitte mindestens ein Foto hinzufügen");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f.file));
      const { data } = await api.post(`/scan/ai?type=${type}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(data);
      const detected = data?.detected_items || [];
      track({
        custom: "ScannerUsed",
        params: {
          detected_count: detected.length,
          photo_count: data?.photo_count || files.length,
          job_type: data?.job_type,
          difficulty: data?.difficulty,
          type,
          estimate_min: data.estimate_min,
          estimate_max: data.estimate_max,
        },
      });
      if (detected.length === 0) {
        toast.message("Keine Möbel klar erkennbar — versuchen Sie klarere oder weitere Fotos.");
      }
    } catch (e) {
      try {
        const { data: fb } = await api.post("/scan", { photo_count: files.length, type });
        setResult({
          detected_items: [],
          item_count: files.length,
          difficulty: "mittel",
          job_type: "few_items",
          notes: "KI-Schätzung nicht verfügbar – grobe Spanne anhand Fotoanzahl.",
          reasons: ["KI nicht erreichbar – grobe Schätzung anhand Fotoanzahl.", "Finaler Preis nach Foto-Prüfung per WhatsApp."],
          per_image: [],
          photo_count: files.length,
          estimate_min: fb.min,
          estimate_max: fb.max,
        });
      } catch {
        toast.error("Schätzung fehlgeschlagen");
      }
    } finally {
      setLoading(false);
    }
  };

  const goToFullQuote = () => {
    if (!result) return;
    const payload = {
      type,
      detected_items: result.detected_items || [],
      estimate_min: result.estimate_min,
      estimate_max: result.estimate_max,
      job_type: result.job_type,
      effort: result.difficulty,
    };
    try {
      sessionStorage.setItem("scan_prefill_v1", JSON.stringify(payload));
    } catch {}
    trackCustom("ScanToQuote", { source: "scan", type });
    navigate(`/angebot?type=${type}`);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-amberDark">
          <Sparkles size={14} /> KI-Schnellscan
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">
          Was könnte Ihr Transport kosten?
        </h1>
        <p className="mt-3 text-slate-600 max-w-xl">
          Mehrere Fotos hochladen oder Kamera öffnen – die KI erkennt alle sichtbaren Gegenstände aus allen Bildern und schätzt einen Gesamt-Preisbereich.
        </p>
        <p className="mt-1 text-xs text-brand-amberDark font-semibold">Bis zu 8 Fotos · final per WhatsApp bestätigt</p>

        <div className="mt-8 rounded-3xl bg-white border border-slate-200 shadow-soft p-6 sm:p-8">
          {/* Type toggle */}
          <div className="flex gap-2 mb-6" data-testid="scan-type">
            <button
              onClick={() => setType("small")}
              className={`flex-1 rounded-xl px-4 py-3 text-sm font-semibold transition-colors border ${
                type === "small" ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
              data-testid="scan-type-small"
            >
              Kleine Transporte
            </button>
            <button
              onClick={() => setType("full")}
              className={`flex-1 rounded-xl px-4 py-3 text-sm font-semibold transition-colors border ${
                type === "full" ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
              data-testid="scan-type-full"
            >
              Kompletter Umzug
            </button>
          </div>

          {/* Camera and upload buttons */}
          <div className="grid grid-cols-2 gap-3">
            <label className="cursor-pointer rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/50 hover:bg-amber-50 py-8 grid place-items-center text-center transition-colors" data-testid="scan-camera">
              <input type="file" accept="image/*" capture="environment" multiple onChange={onPick} className="hidden" />
              <Camera className="text-brand-amberDark mb-2" size={28} />
              <div className="text-sm font-semibold text-slate-800">Kamera öffnen</div>
              <div className="text-xs text-slate-500 mt-0.5">Mehrere Aufnahmen möglich</div>
            </label>
            <label className="cursor-pointer rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/70 hover:bg-slate-100 py-8 grid place-items-center text-center transition-colors" data-testid="scan-upload">
              <input type="file" accept="image/*" multiple onChange={onPick} className="hidden" />
              <Upload className="text-slate-700 mb-2" size={28} />
              <div className="text-sm font-semibold text-slate-800">Fotos hochladen</div>
              <div className="text-xs text-slate-500 mt-0.5">Mehrere Bilder auswählen</div>
            </label>
          </div>

          {files.length > 0 && (
            <div className="mt-5">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                {files.length} Foto{files.length === 1 ? "" : "s"} ausgewählt
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="scan-thumbs">
                {files.map((f, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                    <img src={f.previewUrl} alt="" className="w-full h-full object-cover" />
                    <div className="absolute top-1 left-1 bg-brand-navy/85 text-white text-[10px] font-bold rounded px-1.5 py-0.5">
                      {i + 1}
                    </div>
                    <button
                      onClick={() => removeAt(i)}
                      className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded p-0.5 shadow-soft"
                      aria-label="Entfernen"
                    >
                      <X size={12} className="text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={runScan}
            disabled={loading}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-amber text-brand-navy px-6 py-4 font-bold shadow-soft hover:bg-amber-300 disabled:opacity-60 transition-colors"
            data-testid="scan-run"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
            {loading
              ? "Analyse läuft…"
              : files.length > 1
                ? `${files.length} Fotos analysieren`
                : "Jetzt Preis prüfen"}
          </button>

          {result && (
            <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-5 animate-fade-up" data-testid="scan-result">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Geschätzte Preisspanne</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-[11px] font-semibold border rounded-full px-2 py-0.5 ${EFFORT_COLOR[result.difficulty] || EFFORT_COLOR.mittel}`}>
                    Aufwand · {EFFORT_LABEL[result.difficulty] || "Mittel"}
                  </span>
                  {result.job_type && (
                    <span className="text-[11px] font-semibold bg-white border border-amber-200 text-amber-900 rounded-full px-2 py-0.5">
                      {JOB_TYPE_LABEL[result.job_type] || result.job_type}
                    </span>
                  )}
                  {result.photo_count > 0 && (
                    <span className="text-[11px] font-semibold bg-white border border-amber-200 text-amber-900 rounded-full px-2 py-0.5">
                      {result.photo_count} Foto{result.photo_count === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-1 text-3xl sm:text-4xl font-heading font-extrabold text-slate-900" data-testid="scan-price">
                {result.estimate_min}€ – {result.estimate_max}€
              </div>

              {result.detected_items?.length > 0 ? (
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Erkannte Gegenstände · Gesamt</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="detected-items">
                    {result.detected_items.map((it, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white border border-amber-200 text-amber-900 px-2.5 py-1 text-xs font-semibold">
                        <CheckCircle2 size={12} /> {it}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex items-start gap-2 rounded-xl bg-white/70 border border-amber-200 p-3 text-xs text-slate-700">
                  <AlertCircle size={14} className="mt-0.5 text-amber-700 shrink-0" />
                  <span>Keine Möbel klar erkennbar. Bitte ein helleres / nahes Foto versuchen.</span>
                </div>
              )}

              {Array.isArray(result.per_image) && result.per_image.length > 1 && (
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Pro Foto</div>
                  <div className="space-y-1.5" data-testid="per-image-list">
                    {result.per_image.map((p, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-slate-700">
                        <span className="inline-flex shrink-0 w-5 h-5 rounded bg-brand-navy text-white text-[10px] font-bold items-center justify-center">{p.index || i + 1}</span>
                        <span className="leading-snug">
                          {p.items && p.items.length > 0 ? p.items.join(", ") : <em className="text-slate-500">keine Möbel</em>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(result.reasons) && result.reasons.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Warum diese Spanne?</div>
                  <ul className="space-y-1 text-xs text-slate-700 list-disc list-inside" data-testid="scan-reasons">
                    {result.reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}

              {result.notes && <p className="mt-3 text-xs text-slate-600 italic">{result.notes}</p>}
              <p className="mt-3 text-xs text-slate-600">Preis basiert auf erkannten Gegenständen, geschätztem Aufwand, Entfernung und Etage/Aufzug. Finaler Preis nach Foto-Prüfung per WhatsApp.</p>

              <button
                onClick={goToFullQuote}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-brand-navy text-white px-5 py-3 text-sm font-semibold hover:bg-slate-800 transition-colors"
                data-testid="scan-continue"
              >
                Vollständiges Angebot anfordern <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>

        <p className="mt-6 text-sm text-slate-500 text-center">
          <MessageCircle size={14} className="inline mr-1" />
          Antworten erhalten Sie bequem über WhatsApp.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
